/**
 * MixerChannelStrip Component
 * A reusable, modular mixer channel strip with volume fader, pan knob, mute/solo buttons
 * 
 * Features:
 * - Volume fader with dB scale
 * - Pan knob with L/R indicator
 * - Mute and Solo buttons with visual feedback
 * - Track name display
 * - Peak meter (optional)
 * - Per-track plugin chain with VST-style plugins
 * - Fully controlled component for easy state management
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Volume2, VolumeX, Headphones, MoreVertical, Guitar, Trash2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { TrackPluginChain } from '../plugins/TrackPluginChain';
import { TrackGuitarEffects } from '../effects/TrackGuitarEffects';
import { Button } from '../../ui/button';

// Utility: Convert linear volume (0-1) to dB display
const volumeToDb = (volume) => {
  if (volume === 0) return '-∞';
  const db = 20 * Math.log10(volume);
  return db.toFixed(1);
};

// Utility: Convert dB to linear volume
const dbToVolume = (db) => {
  if (db <= -60) return 0;
  return Math.pow(10, db / 20);
};

/**
 * Volume Fader Component
 * Vertical slider for volume control with dB markings
 */
const VolumeFader = ({ value = 0.8, onChange, className }) => {
  const faderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const handleMouseMove = (moveEvent) => {
      if (!faderRef.current) return;
      const rect = faderRef.current.getBoundingClientRect();
      const y = moveEvent.clientY - rect.top;
      const height = rect.height;
      const newValue = Math.max(0, Math.min(1, 1 - (y / height)));
      onChange?.(newValue);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Initial position update
    handleMouseMove(e);
  }, [onChange]);
  
  const percentage = value * 100;
  
  return (
    <div 
      ref={faderRef}
      className={cn(
        "relative w-8 h-32 bg-zinc-800 rounded-sm cursor-pointer select-none",
        isDragging && "ring-1 ring-cyan-500",
        className
      )}
      onMouseDown={handleMouseDown}
      data-testid="volume-fader"
    >
      {/* Track background */}
      <div className="absolute inset-x-2 inset-y-1 bg-zinc-900 rounded-sm overflow-hidden">
        {/* Filled portion */}
        <motion.div 
          className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-cyan-600 to-cyan-400"
          style={{ height: `${percentage}%` }}
          animate={{ height: `${percentage}%` }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      </div>
      
      {/* Fader handle */}
      <motion.div 
        className={cn(
          "absolute left-0 right-0 h-4 bg-zinc-300 rounded-sm shadow-lg",
          "border border-zinc-400",
          "flex items-center justify-center",
          isDragging && "bg-white"
        )}
        style={{ bottom: `calc(${percentage}% - 8px)` }}
        animate={{ bottom: `calc(${percentage}% - 8px)` }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <div className="w-4 h-0.5 bg-zinc-500 rounded" />
      </motion.div>
      
      {/* dB markers */}
      <div className="absolute -right-6 inset-y-0 flex flex-col justify-between text-[8px] text-zinc-500 py-1">
        <span>+6</span>
        <span>0</span>
        <span>-6</span>
        <span>-12</span>
        <span>-∞</span>
      </div>
    </div>
  );
};

/**
 * Pan Knob Component
 * Rotary control for stereo panning
 */
const PanKnob = ({ value = 0, onChange, size = 40, className }) => {
  const knobRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const startValue = useRef(0);
  
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    startY.current = e.clientY;
    startValue.current = value;
    
    const handleMouseMove = (moveEvent) => {
      const deltaY = startY.current - moveEvent.clientY;
      const sensitivity = 0.01;
      const newValue = Math.max(-1, Math.min(1, startValue.current + deltaY * sensitivity));
      onChange?.(newValue);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [value, onChange]);
  
  // Double-click to reset to center
  const handleDoubleClick = useCallback(() => {
    onChange?.(0);
  }, [onChange]);
  
  // Convert value (-1 to 1) to rotation (-135 to 135 degrees)
  const rotation = value * 135;
  
  // Pan display text
  const panText = value === 0 ? 'C' : value < 0 ? `L${Math.abs(Math.round(value * 100))}` : `R${Math.round(value * 100)}`;
  
  return (
    <div className={cn("flex flex-col items-center gap-1", className)}>
      <div 
        ref={knobRef}
        className={cn(
          "relative rounded-full bg-zinc-800 border-2 border-zinc-600 cursor-pointer",
          isDragging && "border-cyan-500"
        )}
        style={{ width: size, height: size }}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        data-testid="pan-knob"
      >
        {/* Knob indicator */}
        <motion.div 
          className="absolute inset-1 rounded-full bg-gradient-to-br from-zinc-600 to-zinc-700"
          style={{ rotate: rotation }}
          animate={{ rotate: rotation }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {/* Pointer line */}
          <div className="absolute top-1 left-1/2 -translate-x-1/2 w-0.5 h-2 bg-cyan-400 rounded-full" />
        </motion.div>
        
        {/* L/R labels */}
        <span className="absolute -left-4 top-1/2 -translate-y-1/2 text-[8px] text-zinc-500">L</span>
        <span className="absolute -right-4 top-1/2 -translate-y-1/2 text-[8px] text-zinc-500">R</span>
      </div>
      
      {/* Pan value display */}
      <span className="text-[10px] font-mono text-zinc-400">{panText}</span>
    </div>
  );
};

/**
 * Mute/Solo Button Component
 */
const ToggleButton = ({ 
  active, 
  onClick, 
  icon: Icon, 
  activeColor = 'bg-amber-500', 
  label,
  testId,
  className 
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-8 h-6 rounded flex items-center justify-center transition-all",
        "text-[10px] font-bold uppercase",
        active 
          ? `${activeColor} text-white shadow-lg` 
          : "bg-zinc-700 text-zinc-400 hover:bg-zinc-600",
        className
      )}
      data-testid={testId}
    >
      {Icon ? <Icon className="w-3 h-3" /> : label}
    </button>
  );
};

/**
 * Peak Meter Component
 * Visual level indicator
 */
const PeakMeter = ({ level = 0, className }) => {
  const percentage = Math.min(100, level * 100);
  const isClipping = level > 0.95;
  
  return (
    <div className={cn("w-2 h-32 bg-zinc-900 rounded-sm overflow-hidden", className)}>
      <motion.div 
        className={cn(
          "absolute bottom-0 left-0 right-0 rounded-sm",
          isClipping 
            ? "bg-gradient-to-t from-red-600 to-red-400" 
            : "bg-gradient-to-t from-green-600 via-yellow-500 to-red-500"
        )}
        style={{ height: `${percentage}%` }}
        animate={{ height: `${percentage}%` }}
        transition={{ duration: 0.05 }}
      />
    </div>
  );
};

/**
 * Main MixerChannelStrip Component
 * Complete channel strip with all controls
 */
export const MixerChannelStrip = ({
  track,
  index,
  isSelected = false,
  onVolumeChange,
  onPanChange,
  onMuteToggle,
  onSoloToggle,
  onSelect,
  onNameChange,
  onRemoveTrack, // NEW: Delete track
  canRemoveTrack = true, // NEW: Whether track can be removed
  peakLevel = 0,
  showMeter = true,
  // Plugin chain props
  onAddPlugin,
  onRemovePlugin,
  onTogglePluginBypass,
  onReorderPlugins,
  onPluginParamChange,
  onOpenPluginBrowser,
  onOpenTrackPresets, // NEW: Open track preset selector
  // Guitar effects props
  onGuitarEffectsChange,
  guitarEffectsExpanded = false,
  onToggleGuitarEffectsExpand,
  userId = null, // For preset management
  // Legacy effect chain props (for backwards compatibility)
  onAddEffect,
  onRemoveEffect,
  onToggleEffect,
  onReorderEffects,
  onEffectParamChange,
  className,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(track?.name || `Track ${index + 1}`);
  const [effectsExpanded, setEffectsExpanded] = useState(false);
  const inputRef = useRef(null);
  
  // Handle name editing
  const handleNameDoubleClick = () => {
    setIsEditing(true);
    setEditName(track?.name || `Track ${index + 1}`);
  };
  
  const handleNameSubmit = () => {
    setIsEditing(false);
    if (editName.trim() && editName !== track?.name) {
      onNameChange?.(editName.trim());
    }
  };
  
  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditName(track?.name || `Track ${index + 1}`);
    }
  };
  
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  
  // Get track color or default
  const trackColor = track?.color || 'cyan';
  const colorClasses = {
    cyan: 'border-cyan-500/50 bg-cyan-500/5',
    indigo: 'border-indigo-500/50 bg-indigo-500/5',
    purple: 'border-purple-500/50 bg-purple-500/5',
    pink: 'border-pink-500/50 bg-pink-500/5',
    amber: 'border-amber-500/50 bg-amber-500/5',
    green: 'border-green-500/50 bg-green-500/5',
  };
  
  return (
    <motion.div
      className={cn(
        "flex flex-col items-center p-3 rounded-lg border transition-all relative group",
        "bg-zinc-900/80 backdrop-blur-sm",
        isSelected 
          ? "border-cyan-500 ring-1 ring-cyan-500/30" 
          : "border-zinc-700/50 hover:border-zinc-600",
        colorClasses[trackColor] || colorClasses.cyan,
        className
      )}
      onClick={() => onSelect?.()}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      data-testid={`mixer-channel-${track?.id || index}`}
    >
      {/* Track number badge */}
      <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center mb-2">
        <span className="text-[10px] font-bold text-zinc-400">{index + 1}</span>
      </div>
      
      {/* Delete track button */}
      {canRemoveTrack && onRemoveTrack && (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => { e.stopPropagation(); onRemoveTrack(); }}
          className="absolute top-1 right-1 p-1 h-auto text-zinc-500 hover:text-red-400 hover:bg-red-400/10 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Delete Track"
          data-testid={`delete-track-${track?.id || index}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      )}
      
      {/* Mute / Solo buttons */}
      <div className="flex gap-1 mb-3">
        <ToggleButton
          active={track?.muted}
          onClick={(e) => { e.stopPropagation(); onMuteToggle?.(); }}
          label="M"
          activeColor="bg-red-500"
          testId={`mixer-mute-${track?.id || index}`}
        />
        <ToggleButton
          active={track?.solo}
          onClick={(e) => { e.stopPropagation(); onSoloToggle?.(); }}
          label="S"
          activeColor="bg-amber-500"
          testId={`mixer-solo-${track?.id || index}`}
        />
      </div>
      
      {/* Pan knob */}
      <div className="mb-3">
        <PanKnob
          value={track?.pan || 0}
          onChange={onPanChange}
          size={36}
        />
      </div>
      
      {/* Volume fader with optional meter */}
      <div className="flex gap-1 mb-3">
        {showMeter && <PeakMeter level={peakLevel} />}
        <VolumeFader
          value={track?.volume ?? 0.8}
          onChange={onVolumeChange}
        />
      </div>
      
      {/* dB value display */}
      <div className="text-[10px] font-mono text-zinc-400 mb-2">
        {volumeToDb(track?.volume ?? 0.8)} dB
      </div>
      
      {/* Track name */}
      <div 
        className="w-full text-center cursor-pointer"
        onDoubleClick={handleNameDoubleClick}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleNameSubmit}
            onKeyDown={handleNameKeyDown}
            className="w-full px-1 py-0.5 text-[11px] bg-zinc-800 border border-cyan-500 rounded text-center text-white outline-none"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span 
            className="text-[11px] font-medium text-zinc-300 truncate block"
            title={track?.name || `Track ${index + 1}`}
            data-testid={`mixer-track-name-${track?.id || index}`}
          >
            {track?.name || `Track ${index + 1}`}
          </span>
        )}
      </div>
      
      {/* Track type indicator */}
      {track?.type && (
        <span className="text-[8px] uppercase text-zinc-500 mt-1">
          {track.type === 'instrument' ? track.instrumentType || 'inst' : 'audio'}
        </span>
      )}
      
      {/* Plugin Chain */}
      <div className="w-full mt-2 pt-2 border-t border-zinc-700/50">
        <TrackPluginChain
          trackId={track?.id}
          trackName={track?.name}
          plugins={track?.pluginChain || []}
          onAddPlugin={onAddPlugin}
          onRemovePlugin={onRemovePlugin}
          onToggleBypass={onTogglePluginBypass}
          onReorderPlugins={onReorderPlugins}
          onParamChange={onPluginParamChange}
          onOpenBrowser={onOpenPluginBrowser}
          onOpenPresets={onOpenTrackPresets}
          isExpanded={effectsExpanded}
          onToggleExpand={() => setEffectsExpanded(!effectsExpanded)}
          compact={true}
        />
      </div>
      
      {/* Guitar Effects */}
      <div className="w-full mt-2">
        <TrackGuitarEffects
          trackId={track?.id}
          trackName={track?.name}
          guitarEffects={track?.guitarEffects}
          onGuitarEffectsChange={onGuitarEffectsChange}
          isExpanded={guitarEffectsExpanded}
          onToggleExpand={onToggleGuitarEffectsExpand}
          userId={userId}
          currentPresetId={track?.guitarEffects?.presetId}
        />
      </div>
    </motion.div>
  );
};

export default MixerChannelStrip;
